import React, { createContext, useContext, useEffect, useState } from "react";

const CartContext = createContext();

// The cart-item key is `${product.id}::${variant_id || "_"}` so two variants
// of the same product coexist as separate lines (different prices/stock).
// Legacy items saved without a variant_id use the "_" sentinel — they still
// merge correctly with new additions of the same base product.
const cartKey = (productId, variantId) => `${productId}::${variantId || "_"}`;

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try {
      const raw = localStorage.getItem("vt_cart");
      return raw ? JSON.parse(raw) : [];
    } catch { return []; }
  });

  useEffect(() => {
    localStorage.setItem("vt_cart", JSON.stringify(items));
  }, [items]);

  /**
   * Add a product to the cart. Pass a `variant` object (from product.variants)
   * to lock the line to a specific SKU with its own price/stock. Cart items
   * are keyed by (product_id, variant_id) so different variants of the same
   * product coexist as separate lines.
   */
  const addItem = (product, quantity = 1, variant = null) => {
    const key = cartKey(product.id, variant?.id);
    setItems((prev) => {
      const idx = prev.findIndex((it) => it.key === key);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      const opts = variant?.options || {};
      const suffix = Object.values(opts).filter(Boolean).join(" / ");
      // Retail price: variant.price if set, else supplier * 1.30, else product.price
      const variantPrice = variant?.price != null && variant?.price !== ""
        ? parseFloat(variant.price)
        : (variant?.supplier_price != null ? Math.round(parseFloat(variant.supplier_price) * 1.30 * 100) / 100 : null);
      const price = variantPrice != null && !Number.isNaN(variantPrice) ? variantPrice : product.price;
      const image = variant?.image || (product.images || [])[0];
      return [...prev, {
        key,
        id: product.id,
        slug: product.slug,
        name: product.name + (suffix ? ` · ${suffix}` : ""),
        price,
        image,
        quantity,
        variant_id: variant?.id || null,
        variant_sku: variant?.sku || "",
        variant_options: opts,
      }];
    });
  };

  const removeItem = (key) => setItems((prev) => prev.filter((it) => (it.key || it.id) !== key));
  const updateQty = (key, qty) => setItems((prev) => prev.map((it) => (it.key || it.id) === key ? { ...it, quantity: Math.max(1, qty) } : it));
  const clear = () => setItems([]);

  const total = items.reduce((sum, it) => sum + it.price * it.quantity, 0);
  const count = items.reduce((sum, it) => sum + it.quantity, 0);

  return (
    <CartContext.Provider value={{ items, addItem, removeItem, updateQty, clear, total, count }}>
      {children}
    </CartContext.Provider>
  );
}

export const useCart = () => useContext(CartContext);
